import type { DeletePageModalProps } from "@editor/components/AppModalTypes";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import {
  errorToast,
  successToast,
} from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useNavigate } from "react-router-dom";

export const DeletePageModal: React.FC<DeletePageModalProps> = ({ page }) => {
  const projectId = useCurrentProjectId();
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const { mutate: deletePage, isPending } =
    trpc.nextJSProject.deletePage.useMutation({
      onSuccess: () => {
        void trpcUtils.nextJSProject.listPages.invalidate({ projectId });
        void trpcUtils.webContainer.getFileTree.invalidate({ projectId });
        void trpcUtils.funnel.list.invalidate({ projectId });
        void trpcUtils.funnel.find.invalidate({ projectId });

        successToast(
          "Page Deleted",
          `${page.name} has been deleted successfully! 🎉`,
        );

        closeModal({ type: "deletePageModal" });

        if (projectId) {
          navigate(`/shops/${projectId}/home`);
        }
      },
    });

  const handleDelete = () => {
    if (!projectId) {
      return;
    }

    // Check if the page is currently published
    if (page.lastPublishedFileVersionId) {
      errorToast(
        "Cannot Delete Published Page",
        "You must unpublish this page before you can delete it. Published pages cannot be deleted.",
      );
      return;
    }

    deletePage({
      projectId,
      id: page.id,
    });
  };

  return (
    <Modal
      title="Delete Page"
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "deletePageModal" });
        }
      }}
      size="sm"
      description="Are you sure you want to delete this page?"
      footer={
        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="base"
            onClick={() => closeModal({ type: "deletePageModal" })}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="base"
            onClick={handleDelete}
            isLoading={isPending}
            data-testid="delete-page-button"
          >
            Delete
          </Button>
        </div>
      }
    >
      <InlineAlert variant="warning">
        Deleting this page will permanently remove it from your store. You won't
        be able to recover it later.
      </InlineAlert>
    </Modal>
  );
};
